/**
 * Copy + action when the results page has a pool but nothing to render.
 * Classic UI used to set didBroaden and then skip this message — that left a
 * blank "Suggested Legs" area while +EV / Direct links still hid every card.
 */

export function describeFilteredEmpty({
  totalFilteredCount,
  totalCount,
  filterDeepLinksOnly,
  filterPositiveEvOnly,
  filterLineType,
  isSgpResults,
  preferredSportsbook,
  filterSportsbooks,
}) {
  if (totalFilteredCount > 0 || !(totalCount > 0)) return null;

  if (filterLineType === 'alt' || filterLineType === 'main') {
    const which = filterLineType === 'alt' ? 'alternate' : 'main';
    return {
      kind: 'lineType',
      title: filterLineType === 'alt' ? 'No alternate lines in this list' : 'No main lines in this list',
      body: `${totalCount} lines matched the search. None are ${which} lines, so the Lines filter hid every card.`,
      actionLabel: 'Show all lines',
      action: 'clearLineType',
    };
  }

  if (filterDeepLinksOnly && filterPositiveEvOnly) {
    return {
      kind: 'evAndDeepLinks',
      title: 'No +EV bets with direct links',
      body: `${totalCount} lines matched the search. +EV only and Direct links together hid every card.`,
      actionLabel: 'Turn off +EV and Direct links',
      action: 'clearEvAndDeepLinks',
    };
  }

  if (filterPositiveEvOnly) {
    return {
      kind: 'plusEv',
      title: 'No +EV bets in this list',
      body: `${totalCount} lines matched the search. None have a positive EV pill, so +EV only hid every card.`,
      actionLabel: 'Show all EV (including negative)',
      action: 'clearPlusEv',
    };
  }

  if (filterDeepLinksOnly) {
    return {
      kind: 'deeplinks',
      title: 'No direct bet links on these results',
      body: 'These lines may have been cached before deep links were stored. Turn the filter off to see the full list.',
      actionLabel: `Show all ${totalCount} results`,
      action: 'clearDeepLinks',
    };
  }

  const focusBook = Array.isArray(filterSportsbooks) && filterSportsbooks.length === 1
    ? filterSportsbooks[0]
    : null;
  if (isSgpResults && preferredSportsbook && focusBook) {
    return {
      kind: 'focusBook',
      title: `No ${focusBook} SGPs in this list`,
      body: `${focusBook} may not offer same-game parlays for this sport. You can view SGPs from every book instead.`,
      actionLabel: `Show all ${totalCount} SGPs`,
      action: 'clearFocusBook',
    };
  }

  return {
    kind: 'filters',
    title: 'Filters hid every result',
    body: `${totalCount} lines matched the search, but none pass the current filters.`,
    actionLabel: 'Clear filters',
    action: 'clearFilters',
  };
}

/**
 * Zero-pool empty state (backend returned nothing). Distinguishes freeze
 * snapshot misses from real search failures so archive testing stays honest.
 */
export function describeSearchEmpty({
  fetchedCount,
  resultType,
  message,
  freezeMode,
  failureKind,
}) {
  if (fetchedCount > 0) return null;
  const isFreezeMiss = resultType === 'FROZEN_NO_MATCH'
    || failureKind === 'frozen_snapshot_miss'
    || (freezeMode && resultType && String(resultType).includes('NO_MATCH'));
  if (isFreezeMiss) {
    return {
      kind: 'freezeSnapshotMiss',
      title: 'Not in this freeze snapshot',
      body: message
        ? `${message} This is not a search failure — the archive simply does not include that game or team.`
        : 'That game or team was not captured in the frozen archive. This is not a search failure.',
    };
  }
  if (message) {
    return {
      kind: 'backendEmpty',
      title: 'No odds found',
      body: message,
    };
  }
  return null;
}

/** Use after ghost-guard: pool exists, filters kept some, but nothing is renderable. */
export function describeResultsEmpty({
  fetchedCount,
  filteredCount,
  renderableCount,
  filterDeepLinksOnly,
  filterPositiveEvOnly,
  filterLineType,
  isSgpResults,
  preferredSportsbook,
  filterSportsbooks,
}) {
  if (!(fetchedCount > 0) || renderableCount > 0) return null;
  if (filteredCount === 0) {
    return describeFilteredEmpty({
      totalFilteredCount: 0,
      totalCount: fetchedCount,
      filterDeepLinksOnly,
      filterPositiveEvOnly,
      filterLineType,
      isSgpResults,
      preferredSportsbook,
      filterSportsbooks,
    });
  }
  return {
    kind: 'ghost',
    title: 'Cards could not be displayed',
    body: `${filteredCount} lines passed filters, but none have priced sportsbook odds the card can render.`,
    actionLabel: 'Clear filters',
    action: 'clearFilters',
  };
}
